/**
 * F39 (Wave 2C) — admin manual goodwill credit-grant modal (D4).
 *
 * Credit amount (USD minor units, non-zero) + mandatory reason (10–500) →
 * POST /admin/venues/:venueId/placement-credits/grant. The server appends a
 * positive `placement_credit_adjustments` row (increases the venue's remaining
 * credit). Gated server-side on `placement_credits:grant`. Rate-limited.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, CurrencyInput, FormField, Modal, Textarea } from '@bravemove/ui-web';
import { GrantPlacementCreditInputSchema } from '@bravemove/types';

export interface GrantCreditDialogProps {
  readonly open: boolean;
  readonly venueLabel: string;
  readonly currency: string;
  readonly onConfirm: (deltaMinor: number, reason: string) => Promise<void>;
  readonly onClose: () => void;
}

export function GrantCreditDialog({
  open,
  venueLabel,
  currency,
  onConfirm,
  onClose,
}: GrantCreditDialogProps) {
  const { t } = useI18n();
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setAmount(0);
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open]);

  const reasonTrimmed = reason.trim();
  const valid = GrantPlacementCreditInputSchema.safeParse({
    deltaMinor: amount,
    reason: reasonTrimmed,
  }).success;

  const submit = async () => {
    setServerError(null);
    if (!valid) {
      setServerError(
        t('placement:admin.monitor.grant_invalid', {
          defaultValue: 'Enter a non-zero amount and a reason (10–500 characters).',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(amount, reasonTrimmed);
      onClose();
    } catch {
      setServerError(
        t('placement:admin.inventory.error_generic', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('placement:admin.monitor.grant_credit', { defaultValue: 'Grant credit' })}
      description={t('placement:admin.monitor.grant_credit_description', {
        venue: venueLabel,
        defaultValue:
          'Grant goodwill credit to {venue}. Increases their remaining placement credit this period.',
      })}
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="grant-credit-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={submitting || !valid}
            data-testid="grant-credit-confirm"
          >
            {t('placement:admin.monitor.grant_credit', { defaultValue: 'Grant credit' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        data-testid="grant-credit-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField
          label={t('placement:admin.monitor.grant_credit_amount', {
            defaultValue: 'Credit amount',
          })}
        >
          <CurrencyInput
            value={amount}
            currency={currency}
            ariaLabel={t('placement:admin.monitor.grant_credit_amount', {
              defaultValue: 'Credit amount',
            })}
            onChange={(v) => setAmount(v)}
          />
        </FormField>
        <FormField
          label={t('placement:admin.monitor.grant_credit_reason', {
            defaultValue: 'Reason for credit',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="grant-credit-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        {serverError ? (
          <div data-testid="grant-credit-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default GrantCreditDialog;
